import styled from 'styled-components';

interface ButtonProps {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  /**
   * Renders the button with the native `disabled` attribute, which the
   * `&:disabled` block in StyledButton already styles. Optional and additive:
   * callers that omit it get no attribute and behave exactly as before.
   *
   * Native `disabled` rather than `aria-disabled` on purpose. It is what stops
   * the click, and it is also what removes the button from the tab order and from
   * ModalBox's focus trap, so a disabled control cannot be reached by keyboard.
   */
  disabled?: boolean;
}

const StyledButton = styled.button`
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
  background: ${({ theme }) => theme.buttonBg};
  color: ${({ theme }) => theme.buttonText};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.7rem 1.3rem;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1), color 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.buttonText};
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
    color: ${({ theme }) => theme.buttonText};
  }

  /* Two-tone ring so the focus indicator stays legible against both the resting
     button background and the accent fill it takes on hover. */
  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }
  
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    background: ${({ theme }) => theme.buttonBg};
    color: ${({ theme }) => theme.buttonText};
    border-color: rgba(255, 255, 255, 0.04);
  }
  @media (max-width: 480px) {
    width: 100%;
    font-size: 0.95rem;
    padding: 0.7rem 1rem;
  }

  /* This component's class selectors outrank the global reduced-motion rules in
     globalStyles.ts, so the guard has to be repeated here. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;


const Button = ({ label, onClick, type = 'button', className, disabled }: ButtonProps) => {
  return (
    <StyledButton
      onClick={onClick}
      type={type}
      className={className}
      disabled={disabled}
    >

      {label}
    </StyledButton>
  );
};

export default Button;
